import dayjs from "dayjs";
import appSettings from "../settings/appSettings";
import { getMonthName, monthNameDateFormat, parseDate } from "./dateHelpers";

const { feedConfig={} } = appSettings;
const { newFeedDaysLimit=30 } = feedConfig;

export function mapChildData(childData) {
  const birthDateParsed = parseDate(childData.birthdate);
  const birthMonth = (birthDateParsed && birthDateParsed.month) || "";
  const birthDay = (birthDateParsed && birthDateParsed.day) || "";
  const birthYear = (birthDateParsed && birthDateParsed.year) || "";

  const isPublishable = childData.webPublishableFlag;
  const shimmerPicture =
    childData.webPublishableFlag && childData.webPublishableRestrictionsFlag;

  const girlSilhouette =
    "https://wvusstatic.com/images/childsilhouette_girl.svg";
  const boySilhouette = "https://wvusstatic.com/images/childsilhouette_boy.svg";

  const defaultPhotoUrl =
    childData?.gender?.toLowerCase() === "female"
      ? girlSilhouette
      : boySilhouette;

  let thumbnailUrl =
    findChildMedia(childData?.childMedia, (item) => {
      return item.format === "thumb" && item.contentTypeCode === "CGP" && (!item.subtypeCode || item.subtypeCode === "");
    })?.url || "";
  let photoUrl =
    findChildMedia(childData?.childMedia, (item) => {
      return item.format === "web" && item.contentTypeCode === "CGP" && (!item.subtypeCode || item.subtypeCode === "");
    })?.url || "";
  let cgvUrl =
    findChildMedia(childData?.childMedia, (item) => {
      return item.format === "web" && item.contentTypeCode === "CGV";
    })?.url || "";
  let cgvThumbnailUrl =
    findChildMedia(childData?.childMedia, (item) => {
      return item.format === "vthumb" && item.contentTypeCode === "CGV";
    })?.url || "";

  if (!isPublishable) {
    thumbnailUrl = photoUrl = defaultPhotoUrl;
    cgvUrl = cgvThumbnailUrl = "";
  }

  return {
    subject: childData.favoriteSubjectDescription,
    hobby: childData.favoriteGameDescription,
    name: childData.givenName,
    thumbnailUrl: thumbnailUrl,
    photoUrl: photoUrl,
    shimmerPicture,
    cgvUrl: cgvUrl,
    cgvThumbnailUrl: cgvThumbnailUrl,
    age: childData.age,
    gender: childData.gender === "Male" ? "boy" : "girl",
    hisHer: childData.gender === "Male" ? "his" : "her",
    himHer: childData.gender === "Male" ? "him" : "her",
    birthMonth: birthMonth,
    birthDay: birthDay,
    birthYear: birthYear,
    birthMonthName: getMonthName(birthMonth),
    language: childData.languageDescription,
    countryName: childData.countryName,
    projectName: childData.projectName,
  };
}

export function findChildMedia(childMedia, query) {
  const media = childMedia || [];
  return media.find(query);
}

/**
 * Helper Funtion to Parse the Child Media Data and return
 * Type of media (Image & Video URLs) and Attributes Data - Alt\Poster (vThumb)
 * Sorted by Date (newest first)
 */
export function getChildMedia(childMedia) {
  const childMediaData = childMedia.filter((mediaObj) => {
    if (
      mediaObj.format === "web" &&
      ["CGP", "CUP", "CGV", "CUV", "CIP"].includes(mediaObj.contentTypeCode)
    ) {
      return true;
    }
    return false;
  });

  const cMediaAttributes = childMedia.filter((mediaObj) => {
    if (
      (mediaObj.format === "thumb" || mediaObj.format === "vthumb") &&
      ["CGP", "CUP", "CGV", "CUV", "CIP"].includes(mediaObj.contentTypeCode)
    ) {
      return true;
    }
    return false;
  });

  const mediaSetSttributes = cMediaAttributes.reduce(
    (cMediaAtts, childMediaObj) => {
      let mediaDate = new Date(childMediaObj.mediaDate)
        .toLocaleDateString("sv")
        .replace(/\-/g, "");

      let contentType = childMediaObj.contentTypeCode;
      let key = [mediaDate, contentType].join("_");

      let mediaType = ["CGP", "CUP", "CIP"].includes(
        childMediaObj.contentTypeCode
      )
        ? "image"
        : "video";

      if (mediaType === "image") {
        Object.assign(cMediaAtts, {
          [key]: {
            alt: childMediaObj.captionForSupporter,
          },
        });
      } else {
        Object.assign(cMediaAtts, {
          [key]: {
            poster: childMediaObj.url,
          },
        });
      }

      return cMediaAtts;
    },
    {}
  );

  const mediaCaptions = {};
  const mediaSet = childMediaData.reduce((accMedia, childMediaObj) => {
    let mediaDate = new Date(childMediaObj.mediaDate)
      .toLocaleDateString("sv")
      .replace(/\-/g, "");
    let contentType = childMediaObj.contentTypeCode;
    let mediaType = ["CGP", "CUP", "CIP"].includes(
      childMediaObj.contentTypeCode
    )
      ? "image"
      : "video";
    let key = [mediaDate, contentType].join("_");
    Object.assign(accMedia, {
      [key]: { [mediaType]: childMediaObj.url },
    });
    Object.assign(mediaCaptions, {
      [key]: monthNameDateFormat(mediaDate),
    });
    return accMedia;
  }, {});

  const sortedMediaSet = Object.keys(mediaSet)
    .sort((a, b) => {
      if (a < b) {
        return 1;
      }
      return -1;
    })
    .reduce(
      (accMedia, key) => {
        const [type, url] = Object.entries(mediaSet[key])[0];
        accMedia["types"].push(type);
        accMedia["urls"].push(url);
        accMedia["attributes"].push(mediaSetSttributes[key]);
        accMedia["captions"].push(mediaCaptions[key]);
        return accMedia;
      },
      {
        types: [],
        urls: [],
        attributes: [],
        captions: [],
      }
    );

  return sortedMediaSet;
}

export function getIndefiniteArticle(noun) {
  const lowercase_noun = (noun + "").toLowerCase(); 
  const vowels = "aeiou";

  if (vowels.indexOf(lowercase_noun[0]) !== -1) {
    return "an";
  }

  return "a";
}

export function setApiUrl(url) {
  if (url && !url.includes("/api")) {
    return `${url}/api`;
  }

  return url;
}

export function getNormalizedParams(queryString = window.location.search) {
  // Reference: https://stackoverflow.com/questions/67159465/can-urlsearchparams-get-param-case-insensitive
  const params = new URLSearchParams(queryString);
  const normalizedParams = new URLSearchParams();
  for (const [name, value] of params) {
    normalizedParams.append(name.toLowerCase(), value);
  }

  return normalizedParams;
}

export function getChildIdParam() {
  const queryParams = getNormalizedParams();
  const childId = queryParams.get("id");

  return childId;
}

export function getCategoryParam(queryString = window.location.search) {
  const queryParams = getNormalizedParams(queryString);
  const type = queryParams.get("filter_type") !== null ? (queryParams.get("filter_type") + "") : ""; 
  const actor_and_id = queryParams.get("filter_actor") !== null ? (queryParams.get("filter_actor") + "") : ""; 
  const actor = actor_and_id.substring(0, actor_and_id.indexOf(':'));
  const id = actor_and_id.substring(actor_and_id.indexOf(':') + 1);

  let filter_string = actor.toLowerCase();

  if (type !== '') {
    if (filter_string !== '') {
      filter_string = filter_string + ":" + type.toLowerCase();
    } else {
      filter_string = type.toLowerCase();
    }    
  } 

  return {
    actor: actor,
    type: type,
    id: id,
    filter_string: filter_string
  };
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function errorParse(err) {
  if (err.message && err.message.includes("Sorry, that child was not found")) {
    return err.message;
  }

  //to do: find the right default message
  return "Sorry, there was a problem retrieving that child";
}

export const CHILD_RCDS_ERROR_NO_GUID = 1;
export const CHILD_RCDS_ERROR_NOT_FOUND = 2;
export const CHILD_RCDS_ERROR_SYSTEM = 3;

export function parseErrorType(err) {
  if (err.message && err.message.includes("No childId found")) {
    return CHILD_RCDS_ERROR_NO_GUID;
  } else if (
    err.message &&
    err.message.includes("Sorry, that child was not found")
  ) {
    return CHILD_RCDS_ERROR_NOT_FOUND;
  }

  return CHILD_RCDS_ERROR_SYSTEM;
}

export const triggerAnalyticsEvent = (
  eventName = "msc_event",
  eventData = {}
) => {
  const event = new CustomEvent(eventName, {
    detail: eventData
  });
  document.dispatchEvent(event);
};

export const normalizeCategoryKey = (sector) => sector.toLowerCase().replace(/\s/g, '');

export function mapSectorData(sector = "") {
  const normalizedSector = normalizeCategoryKey(sector); // lower case and remove spaces

  const generateTitle = (sector) => `News about ${sector}`;

  const sectorData = {
    "loveofgodandneighbors": 
      {
        "title": generateTitle(sector),
        "text": "Children and families are growing spiritually, local churches are strengthened to demonstrate Christ's love in practical ways, and people are living at peace with their neighbors."
      }, 
    "childprotectionandcare": 
      {
        "title": generateTitle(sector),
        "text": "Boys and girls are safe and valued while participating in their communities as agents of transformation."
      }, 
    "educationforbetterlives": 
      {
        "title": generateTitle(sector),
        "text": "Families have opportunities to expand their talents and learn more through community support."
      }, 
    "healthychildrenandfamilies": 
      {
       "title": generateTitle(sector),
       "text": "Children and families are well nourished, protected from infection and disease, and have access to essential health services."
      },
      "health,waterandnutrition": 
      {
       "title": generateTitle(sector),
       "text": "Children and families are well nourished, protected from infection and disease, and have access to essential health services."
      },
      "economicopportunities":
      {
        "title": generateTitle(sector),
        "text": ""
       },
      "communityempowermentandpartnerships":
      {
        "title": generateTitle(sector),
        "text": ""
       },
       "unknown":
       {
         "title": "Other News",
         "text": ""
        }
  }

  // Return default title and blank text if sector is not found 
  if (!sectorData[normalizedSector]) {
 
    return {
      "title": generateTitle(sector),
      "text": ""
    };
  }

  return sectorData[normalizedSector];
}

export function highlightFirstPhrase(idealWordCount = 2, maxWordCount = 3, sentence = "") {
  const prepositions = ["a", "about", "across", "after", "an", "as", "and", "are", "around", "at", "be", "before", "between", "by", "for", "from", "have", "in", "is", "it", "its", "like", "more", "to", "than", "that", "the", "of", "or", "over", "to", "toward", "under", "will", "with"]
  
  // split sentence up into words
  const words = sentence.split(' ');

  // divide phrase into ideal number of words
  var lastWordPosition = idealWordCount; 
  var phrase = words.slice(0,lastWordPosition).join(' ');

  // see if the last word highlighted is a preposition, if it is & we haven't reached the max word count, continue to highlight
  while (prepositions.includes(words[lastWordPosition -1]) && lastWordPosition < maxWordCount) {
    lastWordPosition++;
    phrase = words.slice(0,lastWordPosition).join(' ');
  }
  
  const remainingSentence = words.slice(lastWordPosition, words.length).join(' ');

  return {
    highlightedPhrase: phrase,
    remainingSentence: remainingSentence
  }
}

export function getCategories(data) {
  const respData = {
    child: new Set(),
    adp: new Set(),
    country: new Set()
  };

  data.map(item => {
    const actor = item?.actor.split(":")[0];
    respData[actor].add(item.type.toLowerCase());
    return item;
  });

  return {
    child: [...respData["child"]],
    adp: [...respData["adp"]],
    country: [...respData["country"]]
  }
}

export function filterFeedByCategory(feedKey, data) {
  // child:key

  let actor, type;
  // split filter into actor and type if both provided 
  if (feedKey.includes(":")) {
    [actor, type] = feedKey.split(":");
  } else {
    if (["child", "adp", "country"].includes(feedKey)) {
      actor = feedKey;
    }else {
      type = feedKey;
    }
  }
  
  return data.filter(item => (type !== undefined && actor !== undefined) ? (item.actor.includes(actor) && item.type.toLowerCase() === type) : (type !== undefined) ? (item.type.toLowerCase() === type) : item.actor.includes(actor));
}

export function capitalizeText(text) {
  return text.split(/ /).map(word => (word.charAt(0).toUpperCase() + word.substring(1).toLowerCase())).join(" ");
}

export function letterTitleMap(itemData) {
  const { body, subjectName } = itemData.data;
  const { typeCode, subTypeCode, subTypeDescription, typeDescription } = body;
  const mapping = [
    {
      typeCode: "CCS",
      subTypeCode: "ST004",
      description: `special thank you letter`,
      subTitle: `${subjectName} has sent you a special thank you letter.`
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST007",
      description: `Christmas card`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST075",
      description: `Christmas card`,
      subTitle: `Merry Christmas! ${subjectName} has sent you a Christmas card.`
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST077",
      description: `letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST078",
      description: `letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST079",
      description: `annual progress report`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST081",
      description: `special letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST082",
      description: `letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST083",
      description: `community manager letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST084",
      description: `letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST085",
      description: `special letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST088",
      description: `special letter`,
      subTitle: ``
    },
    {
      typeCode: "CCS",
      subTypeCode: "ST566",
      description: `special letter`,
      subTitle: ``
    },
    {
      typeCode: "CES",
      subTypeCode: "ST088",
      description: `special letter`,
      subTitle: ``
    },
    {
      typeCode: "CES",
      subTypeCode: "ST091",
      description: `special letter`,
      subTitle: ``
    }
  ];
  
  const strMatch = (str1, str2) => {
    return !!(str1 && str2 && `${str1}`.toLowerCase() === `${str2}`.toLowerCase());
  }

  return mapping.find(item => strMatch(item.typeCode, typeCode) && strMatch(item.subTypeCode, subTypeCode)) || {
    typeCode,
    subTypeCode,
    description: `${typeDescription || subTypeDescription}`,
    subTitle: ``
  };
}

export function getFeedTitle(itemData) {
  const { type, data, actor } = itemData;
  const { title, body, subjectName, childWellBeing } = data;
  const { typeCode, subTypeDescription, typeDescription } = body;
  const [actorType] = actor.split(":");

  switch (type) {
    case "Photo":
      if (actorType === "child") {
        return {
          title: `${subjectName}'s Photo`,
          subTitle: `Hi, I took a photo for you.`
        };
      } else {
        return {
          title: `Community Photo`,
          subTitle: `Photo of ${capitalizeText(subjectName)}`
        };
      }
    case "Video":
      if (actorType === "child") {
        return {
          title: `${subjectName}'s Video`,
          subTitle: `Hi, I made a video for you.`
        };
      } else {
        return {
          title: `Community Video`,
          subTitle: `Video from ${capitalizeText(subjectName)}`
        };
      }
    case "Letter":
      if (/^c[a-z]+s/i.test(typeCode)) {
        const { description, subTitle } = letterTitleMap(itemData);
        const aOrAn = /^(a|o|i|u|e)/i.test(description) ? "an" : "a";
        return {
          title: `You've received ${aOrAn} ${description}`,
          subTitle: subTitle
        };
      } else {
        const description = ["cew"].includes(subTypeDescription.toLowerCase()) ? "letter" : subTypeDescription || typeDescription;
        return {
          title: `See the ${description}`,
          subTitle: ""
        };
      }
    case "Prayer Request":
      return {
        title,
        subTitle: ""
      };
    case "Accomplishment":
      return {
        title: `Community News on ${childWellBeing}`,
        subTitle: ""
      };
    default: 
      return {
        title,
        subTitle: ""
      };
  }
}

export const getLocalFeedStore = (storageKey) => {
  if (!window.localStoreNewFeeds) {
    // We Want To Store The Cache Before Local Storage Is Modified
    const localStoreVal = localStorage.getItem(storageKey);
    const localStore = localStoreVal ? localStoreVal.split(",") : [];
    window.localStoreNewFeeds = localStore;
  }

  return window.localStoreNewFeeds;
};

export function isNewFeed(feedItem) {
  const { displayTime } = feedItem.data;
  const limitDate = dayjs().subtract(newFeedDaysLimit, 'day');
  const feedCardDate = dayjs(displayTime);
  const isInDateLimit = feedCardDate >= limitDate;

  if (!isInDateLimit) {
    return false;
  }

  // Check Local Storage 
  const id = getChildIdParam();
  const storageKey = `new-feed-viewed:${window.location.pathname.replace(/\//g, "")}:${id}`;
  const localStoreList = getLocalFeedStore(storageKey);
  const isNewFeedCard = !localStoreList.includes(`${displayTime}`);
  
  if (isNewFeedCard) {
    // Add To Local Storage And Return True
    const currentLocalStoreVal = localStorage.getItem(storageKey);
    const currentLocalStore = currentLocalStoreVal ? currentLocalStoreVal.split(",") : [];
    const newlocalStoreList = currentLocalStore.concat([displayTime]);
    const localStoreValue = [...new Set(newlocalStoreList)].join(",");
    localStorage.setItem(storageKey, localStoreValue);
    return true;
  }

  return false;
}

export function addIsNewFeed(feedItem) {
  return {
    ...feedItem,
    isNew: isNewFeed(feedItem)
  };
}